import { create } from 'zustand';
import type { HandId } from './handStore';

/**
 * Finite-state gesture mode per hand.
 *   idle     — no interaction
 *   pluck    — armed to pluck strings
 *   bend     — holding a bend (anchor set, delta tracked)
 *   mute     — palm closed, mutes sounding strings
 */
export type GestureMode = 'idle' | 'pluck' | 'bend' | 'mute';

export interface GestureState {
  mode: GestureMode;
  /** When we entered the current mode (ms) — used for stickiness. */
  enteredAt: number;
  /** Horizontal anchor captured when a bend starts (normalized).
   *  NOTE: live per-frame bend *cents* live in `engine/gesture/liveBend`,
   *  NOT here — writing them 30×/s to Zustand re-rendered the HUD. */
  bendAnchorX: number | null;
}

interface GestureStoreState {
  left: GestureState;
  right: GestureState;

  setMode: (hand: HandId, mode: GestureMode, now: number) => void;
  setBendAnchor: (hand: HandId, anchorX: number | null) => void;
  reset: (hand: HandId) => void;
}

const initial = (): GestureState => ({
  mode: 'idle',
  enteredAt: 0,
  bendAnchorX: null,
});

export const useGestureStore = create<GestureStoreState>((set) => ({
  left: initial(),
  right: initial(),

  setMode: (hand, mode, now) =>
    set((s) => {
      const prev = hand === 'left' ? s.left : s.right;
      if (prev.mode === mode) return s;
      const next: GestureState = { ...prev, mode, enteredAt: now };
      return hand === 'left' ? { left: next } : { right: next };
    }),

  setBendAnchor: (hand, bendAnchorX) =>
    set((s) => {
      const prev = hand === 'left' ? s.left : s.right;
      return hand === 'left'
        ? { left: { ...prev, bendAnchorX } }
        : { right: { ...prev, bendAnchorX } };
    }),

  reset: (hand) =>
    set(hand === 'left' ? { left: initial() } : { right: initial() }),
}));
